import React from "react";
import DefaultLayout from "../../layout/DefaultLayout";
import { useQuery, useAction, getAllReports, getAllFeedbacks, updateReportStatus } from "wasp/client/operations";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { Badge } from "../../../components/ui/badge";
import { Card, CardContent, CardHeader } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import LoadingSpinner from "../../layout/LoadingSpinner";
import { format } from "date-fns";
import { Flag, MessageSquare, ExternalLink } from "lucide-react";
import { toast } from "../../../hooks/use-toast";

const ReportsPage = ({ user }: { user: any }) => {
    const { data: reports, isLoading: isLoadingReports, refetch: refetchReports } = useQuery(getAllReports);
    const { data: feedbacks, isLoading: isLoadingFeedbacks } = useQuery(getAllFeedbacks);

    const updateStatusAction = useAction(updateReportStatus);

    const handleUpdateStatus = async (id: string, status: 'PENDING' | 'REVIEWED' | 'RESOLVED' | 'DISMISSED') => {
        try {
            await updateStatusAction({ id, status });
            toast({ title: "Status updated" });
            refetchReports();
        } catch (error: any) {
            toast({ title: "Error", description: error.message, variant: "destructive" });
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "PENDING": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
            case "RESOLVED": return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
            case "DISMISSED": return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400";
            case "REVIEWED": return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
            default: return "bg-gray-100";
        }
    };

    return (
        <DefaultLayout user={user}>
            <div className="p-6 space-y-6">
                <div>
                    <h1 className="text-3xl font-bold">Reporting & Feedback</h1>
                    <p className="text-muted-foreground">Manage user reports and review platform feedback.</p>
                </div>

                <Tabs defaultValue="reports" className="w-full">
                    <TabsList className="bg-muted">
                        <TabsTrigger value="reports" className="flex gap-2">
                            <Flag className="h-4 w-4" /> Reports
                        </TabsTrigger>
                        <TabsTrigger value="feedbacks" className="flex gap-2">
                            <MessageSquare className="h-4 w-4" /> Feedback
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="reports" className="mt-6 space-y-4">
                        {isLoadingReports ? <LoadingSpinner /> : (
                            !reports || reports.length === 0 ? <p className="text-muted-foreground p-8 text-center border rounded-lg border-dashed">No reports found.</p> : (
                                reports.map((report: any) => (
                                    <Card key={report.id} className="overflow-hidden border-l-4" style={{ borderLeftColor: report.status === 'PENDING' ? '#eab308' : '#e5e7eb' }}>
                                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                            <div className="flex items-center gap-2">
                                                <Badge variant="secondary" className={getStatusColor(report.status)}>{report.status}</Badge>
                                                <span className="text-sm font-semibold uppercase tracking-tight text-muted-foreground">
                                                    {report.targetType}
                                                </span>
                                                {report.sectionKey && (
                                                    <Badge variant="outline" className="capitalize">Section: {report.sectionKey.replace(/-/g, ' ')}</Badge>
                                                )}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                {format(new Date(report.createdAt), "PPP p")}
                                            </div>
                                        </CardHeader>
                                        <CardContent className="pt-2">
                                            <div className="space-y-4">
                                                <div>
                                                    <p className="text-base font-semibold text-foreground">{report.reason}</p>
                                                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{report.details || "No additional details provided."}</p>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs bg-muted/40 p-4 rounded-lg border">
                                                    <div>
                                                        <p className="font-bold uppercase text-[10px] text-muted-foreground mb-1">Reporter</p>
                                                        <p>{report.user?.email || report.user?.username || "Anonymous"}</p>
                                                    </div>
                                                    <div>
                                                        <p className="font-bold uppercase text-[10px] text-muted-foreground mb-1">Location Context</p>
                                                        <p>{report.location?.name ? `${report.location.name} (${report.location.locationCode})` : "N/A"}</p>
                                                    </div>
                                                    {report.content && (
                                                        <div className="col-span-full border-t pt-2 mt-1">
                                                            <p className="font-bold uppercase text-[10px] text-muted-foreground mb-1">Reported Post</p>
                                                            <p className="font-medium text-primary">{report.content.title}</p>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="flex gap-2 justify-end pt-2">
                                                    {report.status === "PENDING" && (
                                                        <>
                                                            <Button size="sm" variant="outline" onClick={() => handleUpdateStatus(report.id, "DISMISSED")}>
                                                                Dismiss
                                                            </Button>
                                                            <Button size="sm" variant="secondary" onClick={() => handleUpdateStatus(report.id, "REVIEWED")} className="mr-1">
                                                                Mark Reviewed
                                                            </Button>
                                                            <Button size="sm" onClick={() => handleUpdateStatus(report.id, "RESOLVED")}>
                                                                Mark Resolved
                                                            </Button>
                                                        </>
                                                    )}
                                                    {(report.status === "REVIEWED" || report.status === "DISMISSED") && (
                                                        <Button size="sm" onClick={() => handleUpdateStatus(report.id, "RESOLVED")}>
                                                            Mark Resolved
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))
                            )
                        )}
                    </TabsContent>

                    <TabsContent value="feedbacks" className="mt-6 space-y-4">
                        {isLoadingFeedbacks ? <LoadingSpinner /> : (
                            !feedbacks || feedbacks.length === 0 ? <p className="text-muted-foreground p-8 text-center border rounded-lg border-dashed">No feedback found.</p> : (
                                feedbacks.map((fb: any) => (
                                    <Card key={fb.id} className="hover:shadow-md transition-shadow">
                                        <CardHeader className="pb-2 border-b bg-muted/20">
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                                                        {(fb.user?.email || "A")[0].toUpperCase()}
                                                    </div>
                                                    <span className="text-sm font-semibold">{fb.user?.email || fb.user?.username || "Anonymous User"}</span>
                                                </div>
                                                <span className="text-xs text-muted-foreground">{format(new Date(fb.createdAt), "PPP p")}</span>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="pt-4">
                                            <p className="text-sm leading-relaxed text-foreground/90 bg-primary/5 p-4 rounded-lg border border-primary/10">{fb.content}</p>
                                            <div className="mt-4 flex flex-wrap gap-4 text-xs text-muted-foreground">
                                                {fb.location && (
                                                    <div className="flex items-center gap-1">
                                                        <Badge variant="secondary" className="text-[10px] h-5">Location: {fb.location.name}</Badge>
                                                    </div>
                                                )}
                                                {fb.pageUrl && (
                                                    <a
                                                        href={fb.pageUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-1 hover:text-primary transition-colors py-1 px-2 bg-muted rounded border"
                                                    >
                                                        <ExternalLink className="h-3 w-3" />
                                                        <span>Source Page</span>
                                                    </a>
                                                )}
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))
                            )
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </DefaultLayout>
    );
};

export default ReportsPage;
